import { Card, Tag, Tooltip, Typography } from 'antd';
import {
  ClockCircleOutlined,
  MessageOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { formatDateWithZone } from './format-date';

export interface EngagementSummaryData {
  is_active: boolean;
  created_at: string;
  next_evaluation_at?: string | null;
  message_count?: number;
  ai_message_count?: number;
  action_plan_name?: string;
  campaign_key?: string | null;
  lead?: {
    last_communication?: {
      created_at: string;
    };
  };
}

interface EngagementSummaryProps {
  engagement: EngagementSummaryData;
  timezone: string;
}

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: 500,
  marginBottom: '4px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#262626',
  fontWeight: 600,
  lineHeight: 1.3,
};

const countStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: 1.1,
};

const DateCell = ({
  icon,
  label,
  timezone,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  timezone: string;
  value?: string | null;
}) => (
  <div className="engagement-summary-cell">
    <div style={labelStyle}>
      {icon}
      {label}
    </div>
    {value ? (
      <Tooltip title={timezone}>
        <div style={valueStyle}>{formatDateWithZone(value, timezone)}</div>
      </Tooltip>
    ) : (
      <div style={{ ...valueStyle, color: '#bfbfbf', fontWeight: 500 }}>
        Not available
      </div>
    )}
  </div>
);

export const EngagementSummary = ({
  engagement,
  timezone,
}: EngagementSummaryProps) => (
  <Card className="engagement-summary-card" size="small">
    <div
      className="engagement-summary-grid"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
      }}
    >
      <div className="engagement-summary-cell engagement-summary-status">
        <div style={labelStyle}>Status</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag
            color={engagement.is_active ? 'success' : 'error'}
            style={{ borderRadius: '6px', margin: 0, fontWeight: 500 }}
          >
            {engagement.is_active ? 'Active' : 'Inactive'}
          </Tag>
        </div>
        {engagement.campaign_key ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              marginTop: 6,
            }}
          >
            <Typography.Text style={{ fontSize: 12 }} type="secondary">
              <strong>Campaign</strong> · {engagement.campaign_key}
            </Typography.Text>
          </div>
        ) : null}
      </div>

      <div className="engagement-summary-cell">
        <div style={labelStyle}>
          <MessageOutlined />
          Lead Replies
        </div>
        <div style={{ ...countStyle, color: '#52c41a' }}>
          {engagement.message_count || 0}
        </div>
      </div>

      <div className="engagement-summary-cell">
        <div style={labelStyle}>
          <RobotOutlined />
          AI Sent
        </div>
        <div style={{ ...countStyle, color: '#1890ff' }}>
          {engagement.ai_message_count || 0}
        </div>
      </div>

      <DateCell
        icon={<ClockCircleOutlined />}
        label="Created"
        timezone={timezone}
        value={engagement.created_at}
      />
      <DateCell
        icon={<ClockCircleOutlined />}
        label="Last Contact"
        timezone={timezone}
        value={engagement.lead?.last_communication?.created_at}
      />
      <DateCell
        icon={<ThunderboltOutlined />}
        label="Next Evaluation"
        timezone={timezone}
        value={engagement.next_evaluation_at}
      />
    </div>
  </Card>
);
